import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { IMAGES } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { ProductCard } from '../components/commerce/ProductCard';
import { StoreCard } from '../components/commerce/StoreCard';
import { fileUrl } from '../lib/upload';

const STORIES = [
  { kicker: 'Craft', title: 'Batik, block by block', body: 'Hand-drawn and block-printed in Kampung Kraftangan, Kelantan batik carries motifs passed down for generations. Every metre is waxed, dyed and dried under the east coast sun.', image: IMAGES.batikCraft },
  { kicker: 'Food', title: 'From Bachok waters to your table', body: 'Keropok ikan, nasi kerabu and akok made the way mak cik has always made them — sourced from coastal villages and morning markets across the state.', image: IMAGES.foodPlate },
  { kicker: 'Fashion', title: 'Songket meets modern modest wear', body: 'Kelantanese weavers and young designers are reimagining songket and batik for everyday wear — heritage you can put on in the morning.', image: IMAGES.batik2 },
];

export default function MadeInKelantanPage() {
  const { customProducts, customMerchants, madeInKelantan } = useApp();
  const heroCfg = madeInKelantan?.hero;
  const heroImageSrc = heroCfg?.image || madeInKelantan?.heroImage;
  const heroImage = heroImageSrc ? fileUrl(heroImageSrc) : IMAGES.batik1;
  const heroTitle = heroCfg?.title || 'Made in Kelantan';
  const heroSubtitle = heroCfg?.subtitle || "From hand-drawn batik in Kampung Kraftangan to keropok ikan from Bachok — every purchase supports a Kelantan maker.";
  const heroKicker = heroCfg?.kicker || 'Featured Brand Story';
  const stories = STORIES.map((s, i) => {
    const o = madeInKelantan?.stories?.[i];
    return o ? { kicker: o.kicker || s.kicker, title: o.title || s.title, body: o.body || s.body, image: o.image ? fileUrl(o.image) : s.image } : s;
  });
  const local = customProducts.filter((p) => p.madeInKelantan);
  const byCat = (cats) => local.filter((p) => cats.includes(p.category));
  const brands = customMerchants.filter((m) => m.plan === 'BRAND');
  const sections = [
    { title: 'Local Food', items: byCat(['Local Food', 'Food & Beverage', 'Bakery']) },
    { title: 'Fashion', items: byCat(['Fashion']) },
    { title: 'Gifts & Handmade', items: byCat(['Gifts']) },
  ];
  return (
    <div data-testid="made-in-kelantan-page">
      <div className="border-b border-kelate-line bg-kelate-darkwood">
        <div className="mx-auto grid max-w-[1280px] items-center gap-8 px-4 py-14 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-kelate-wood">{heroKicker}</p>
            <h1 className="mt-3 font-display text-4xl font-semibold text-kelate-surface sm:text-5xl" data-testid="mik-hero-title">{heroTitle}</h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-[#CBBFA9] md:text-base">
              {heroSubtitle}
            </p>
          </div>
          <img src={heroImage} alt="Kelantan batik" className="hidden h-64 w-full rounded-3xl object-cover shadow-[0_12px_32px_rgba(0,0,0,0.25)] lg:block" />
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10" data-testid="mik-stories">
        {stories.map((s, i) => (
          <article key={s.title} data-testid={`mik-story-${s.kicker.toLowerCase()}`} className={`grid items-center gap-6 py-8 md:grid-cols-2 ${i > 0 ? 'border-t border-kelate-line/60' : ''}`}>
            <img src={fileUrl(s.image)} alt={s.title} className={`h-56 w-full rounded-3xl object-cover shadow-[0_8px_24px_rgba(81,68,55,0.14)] md:h-72 ${i % 2 === 1 ? 'md:order-2' : ''}`} />
            <div className={i % 2 === 1 ? 'md:order-1' : ''}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-kelate-wood">{s.kicker}</p>
              <h2 className="mt-2 font-display text-2xl font-semibold text-kelate-darkwood sm:text-3xl">{s.title}</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-kelate-soft">{s.body}</p>
            </div>
          </article>
        ))}
      </div>

      {brands.length > 0 && (
        <section className="mx-auto max-w-[1280px] px-4 py-8" data-testid="mik-brands">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2 className="text-base md:text-lg font-semibold text-kelate-ink">Brand Partners</h2>
              <p className="text-sm text-kelate-soft">BRAND plan makers building Kelantan's names</p>
            </div>
            <Link to="/marketplace" className="flex items-center gap-1 text-sm font-medium text-kelate-wood hover:text-kelate-darkwood">See all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {brands.map((m) => <StoreCard key={m.id} merchant={m} />)}
          </div>
        </section>
      )}

      {sections.map((s) => s.items.length > 0 && (
        <section key={s.title} data-testid={`mik-${s.title.toLowerCase().replace(/[\s&]+/g, '-')}`} className="mx-auto max-w-[1280px] px-4 py-8">
          <h2 className="mb-4 text-base md:text-lg font-semibold text-kelate-ink">{s.title}</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {s.items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
